import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { format } from "date-fns";

import { useUser } from "../../context/UserContext";
import { getAllAlerts, getMyAlerts } from "../../services/alerts/alertsService";
import CustomPaginator from "../../shared/components/paginator/CustomPaginator";
import InputSearch from "../../shared/components/inputSearch/InputSearch";
import SelectStatus from "../../shared/components/selectStatus/SelectStatus";
import ButtonFilter from "../../shared/components/buttonFilter/ButtonFilter";
import TableHeader from "../../shared/components/tableHeader/TableHeader";
import {
    ALERT_STATUS, ALERT_TYPE, CHANNEL, MONITORED_ALERTS, NOTIFICATION_STATUS, SEVERITY, isAdminUser,
} from "./alertLabels";

const ITEMS_PER_PAGE = 10;
const USER_HEADERS = ['Alerta', 'Sensor', 'Severidad', 'Estado', 'Detectada', 'Última detección'];
const ADMIN_HEADERS = [...USER_HEADERS, 'Notificaciones'];
const STATUS_OPTIONS = [
    { label: 'Activas', value: 'OPEN' },
    { label: 'Resueltas', value: 'RESOLVED' },
];
const DAYS_OPTIONS = [
    { label: 'Últimos 7 días', value: 7 },
    { label: 'Últimos 30 días', value: 30 },
    { label: 'Últimos 90 días', value: 90 },
];

const formatDate = (value) => (value ? format(new Date(value), 'dd/MM/yyyy HH:mm') : '-');

const Badge = ({ config, fallback }) => (
    <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${config?.className || 'bg-gray-100 text-gray-700'}`}>
        {config?.label || fallback}
    </span>
);

const NotificationsCell = ({ notifications, wrap = false }) => {
    if (!notifications || notifications.length === 0) {
        return <span className="text-gray-400">Sin notificaciones</span>;
    }
    return (
        <ul className="space-y-1">
            {notifications.map((n, index) => (
                <li key={index} className={wrap ? 'break-all' : 'whitespace-nowrap'} title={n.error || ''}>
                    {n.userEmail} · {CHANNEL[n.channel] || n.channel} ·{' '}
                    <span className={NOTIFICATION_STATUS[n.status]?.className}>
                        {NOTIFICATION_STATUS[n.status]?.label || n.status}
                    </span>
                    {/* En móvil no hay tooltip: el motivo se muestra debajo */}
                    {wrap && n.error && <p className="text-gray-500 italic">{n.error}</p>}
                </li>
            ))}
        </ul>
    );
};

const AlertRows = React.memo(({ alerts, error, isAdmin, columns }) => {
    if (error || alerts.length === 0) {
        return (
            <tr className="bg-gray-50 border-b text-gray-800">
                <td colSpan={columns} className="text-center px-6 py-3 whitespace-nowrap">
                    {error ? 'Error obteniendo los datos' : 'No se encontraron alertas'}
                </td>
            </tr>
        );
    }

    return alerts.map((alert) => (
        <tr key={alert.id} className="bg-gray-50 border-b text-gray-800 hover:bg-gray-100 align-top">
            <td className="px-6 py-3">
                <p className="font-semibold">{alert.title}</p>
                <p className="text-xs text-gray-500">{ALERT_TYPE[alert.type] || alert.type}</p>
                <p className="text-xs text-gray-600 mt-1">{alert.description}</p>
            </td>
            <td className="px-3 py-3 w-32 break-words">{alert.sensorName}</td>
            <td className="px-6 py-3"><Badge config={SEVERITY[alert.severity]} fallback={alert.severity} /></td>
            <td className="px-6 py-3"><Badge config={ALERT_STATUS[alert.status]} fallback={alert.status} /></td>
            <td className="px-6 py-3 whitespace-nowrap">{formatDate(alert.firstDetectedAt)}</td>
            <td className="px-6 py-3 whitespace-nowrap">{formatDate(alert.lastDetectedAt)}</td>
            {isAdmin && (
                <td className="px-6 py-3 text-xs"><NotificationsCell notifications={alert.notifications} /></td>
            )}
        </tr>
    ));
});

// Qué alertas evalúa la plataforma automáticamente (se despliega con el ícono de información)
const MonitoredAlertsPanel = () => (
    <div className="mb-4 p-4 bg-blue-50 border border-blue-100 rounded-lg text-sm text-gray-800">
        <p className="font-semibold mb-3">Alertas que se monitorean automáticamente</p>
        <ul className="space-y-3">
            {MONITORED_ALERTS.map((alert) => (
                <li key={alert.name} className="flex items-start">
                    <span className="material-icons-outlined text-blue-600 mr-2">{alert.icon}</span>
                    <div>
                        <p className="font-semibold">{alert.name}</p>
                        <p className="text-gray-600">{alert.condition}</p>
                        <p className="text-xs text-gray-500">{alert.schedule}</p>
                    </div>
                </li>
            ))}
        </ul>
        <p className="mt-3 text-xs text-gray-600">
            Además, los umbrales que se configuren para cada sensor generan una alerta por correo cuando una lectura los supera.
        </p>
    </div>
);

// Vista móvil: una tarjeta por alerta en lugar de la tabla
const AlertCards = React.memo(({ alerts, error, isAdmin }) => {
    if (error || alerts.length === 0) {
        return (
            <p className="text-center text-sm text-gray-800 py-3">
                {error ? 'Error obteniendo los datos' : 'No se encontraron alertas'}
            </p>
        );
    }

    return (
        <div className="space-y-3">
            {alerts.map((alert) => (
                <div key={alert.id} className="bg-gray-50 border border-gray-200 rounded-lg shadow p-3 text-sm text-gray-800">
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            <p className="font-semibold">{alert.title}</p>
                            <p className="text-xs text-gray-500">{ALERT_TYPE[alert.type] || alert.type} · {alert.sensorName}</p>
                        </div>
                        <Badge config={ALERT_STATUS[alert.status]} fallback={alert.status} />
                    </div>

                    <p className="text-xs text-gray-600 mt-2">{alert.description}</p>

                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-gray-500">
                        <Badge config={SEVERITY[alert.severity]} fallback={alert.severity} />
                        <span>Detectada: {formatDate(alert.firstDetectedAt)}</span>
                        <span>Última: {formatDate(alert.lastDetectedAt)}</span>
                    </div>

                    {isAdmin && (
                        <div className="mt-2 pt-2 border-t border-gray-200 text-xs">
                            <p className="font-semibold text-gray-700 mb-1">Notificaciones</p>
                            <NotificationsCell notifications={alert.notifications} wrap />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
});

const AlertsView = () => {
    const { user } = useUser();
    const isAdmin = isAdminUser(user);
    const { register, handleSubmit } = useForm({ defaultValues: { statusFilter: '', daysFilter: 30, sensorFilter: '' } });

    const [alerts, setAlerts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({ days: 30 });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showMonitored, setShowMonitored] = useState(false);

    const loadAlerts = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { page: currentPage, limit: ITEMS_PER_PAGE, ...filters };
            const data = isAdmin ? await getAllAlerts(params) : await getMyAlerts(params);
            setAlerts(data.content);
            setTotalPages(Math.max(data.totalPages, 1));
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    }, [currentPage, filters, isAdmin]);

    useEffect(() => {
        if (user) loadAlerts();
    }, [user, loadAlerts]);

    const handleFilterSubmit = handleSubmit((data) => {
        const newFilters = { days: data.daysFilter };
        if (data.statusFilter) newFilters.status = data.statusFilter;
        if (isAdmin && data.sensorFilter) newFilters.sensorId = data.sensorFilter.trim();
        setCurrentPage(1);
        setFilters(newFilters);
    });

    const handlePageChange = useCallback((page) => {
        if (page === currentPage || page < 1 || page > totalPages) return;
        setCurrentPage(page);
    }, [currentPage, totalPages]);

    const headers = isAdmin ? ADMIN_HEADERS : USER_HEADERS;

    return (
        <div className="flex flex-col p-3">
            <div className="flex items-center mb-4">
                <h1 className="text-3xl font-semibold text-blue-600">Alertas</h1>
                <button
                    type="button"
                    onClick={() => setShowMonitored((show) => !show)}
                    className="ml-2 flex items-center text-gray-500 hover:text-blue-600"
                    aria-label="Qué alertas se monitorean"
                    aria-expanded={showMonitored}
                >
                    <span className="material-icons-outlined text-xl">info</span>
                </button>
            </div>

            {showMonitored && <MonitoredAlertsPanel />}

            <form onSubmit={handleFilterSubmit} className="flex flex-col md:flex-row items-center justify-between space-y-3 md:space-y-0 md:space-x-4 mb-2">
                {isAdmin && (
                    <div className="flex items-center w-full md:w-1/4">
                        <InputSearch placeholder="Id del sensor" id="sensorFilter" {...register('sensorFilter')} />
                    </div>
                )}
                <div className="flex items-center w-full md:w-1/4">
                    <SelectStatus id="statusFilter" placeholder="Todas" options={STATUS_OPTIONS} {...register('statusFilter')} />
                </div>
                <div className="flex items-center w-full md:w-1/4">
                    <SelectStatus id="daysFilter" options={DAYS_OPTIONS} {...register('daysFilter')} />
                </div>
                <div className="flex items-center justify-end w-full md:w-1/4">
                    <ButtonFilter type="submit" />
                </div>
            </form>

            <hr className="my-2" />

            {isLoading ? (
                <div className="text-center py-4">Cargando...</div>
            ) : (
                <>
                    <div className="md:hidden">
                        <AlertCards alerts={alerts} error={error} isAdmin={isAdmin} />
                    </div>
                    <div className="hidden md:block relative overflow-x-auto shadow-lg sm:rounded-lg">
                        <table className="w-full text-sm text-left border border-gray-200">
                            <TableHeader headerOptions={headers} />
                            <tbody>
                                <AlertRows alerts={alerts} error={error} isAdmin={isAdmin} columns={headers.length} />
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {!error && (
                <CustomPaginator currentPage={currentPage} totalPages={totalPages} handlePageChange={handlePageChange} />
            )}
        </div>
    );
};

export default AlertsView;
